﻿import React, { useState, useRef, useEffect } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion } from 'framer-motion';

import definitions from '../Style/LfStyleDefinitions';
import { iconCode, mediaQuery, clearFix } from '../Style/cssHelpers';
import { scrollToElement } from '../Utils/scroll';


const Accordion = ({
    unexpandable,
    customHeader,
    id,
    title,
    labelRightComponent,
    labelRight,
    headerComponent,
    footerComponent,
    labelReadMore,
    hideOverflow,
    noHeaderPadding,
    children,
    noBackground,
    extraBottomMargin,
    customMargin,
    isOpen,
    expandHeight
}) => {
    const [open, setOpen] = useState(false);
    const [expandAreaHeight, setExpandAreaHeight] = useState(0);
    const mainContainerEl = useRef(null);
    const expEl = useRef(null);
    const { ref, height } = useResizeDetector();

    useEffect(() => {
        if (!unexpandable) {
            setExpandAreaHeight(expEl.current.clientHeight);
        }
    }, []);

    useEffect(() => {
        if (expEl.current && expandAreaHeight !== expEl.current.clientHeight) {
            setExpandAreaHeight(expEl.current.clientHeight);
        }
    }, [children]);

    const toggleAccordion = (event) => {
        event.preventDefault();

        if (open && expandAreaHeight > window.innerHeight / 2) {
            scrollToElement(mainContainerEl.current, 300);
        }

        setOpen(!open);
    };


    return (
        <BoxWrapper id={id} extraBottomMargin={unexpandable}>
            {customHeader ||
                <HeaderWrapper>
                    <HeaderTitle align="left">{title}</HeaderTitle>
                    {labelRightComponent && labelRightComponent}
                    {labelRight &&
                        <HeaderTitle align="right">{labelRight.text}: <span>{labelRight.value}kr</span></HeaderTitle>
                    }
                </HeaderWrapper>}

            {headerComponent &&
                <Container hideOverflow={hideOverflow} noPadding={noHeaderPadding}>
                    {(hideOverflow || noBackground) || <ContainerBackground />}
                    {headerComponent}
                </Container>
            }

            {footerComponent &&
                footerComponent
            }
            <motion.div
                initial={{ maxHeight: 0 }}
                animate={{ maxHeight: isOpen ? expandHeight * 1.2 : 0 }}
                transition={{ type: "spring", stiffness: 188, damping: 26 }}
                ref={el => { mainContainerEl.current = el; }}
                data-lf-accordion-body
            >
                <div style={{ display: 'block', width: '100%' }} ref={(el) => { expEl.current = el; }}>
                    <div ref={ref}>
                        {children}
                    </div>

                </div>
            </motion.div>


            {!unexpandable && !customHeader &&
                <ReadMore isOpen={isOpen} customMargin={customMargin}>
                    <a href="#" onClick={toggleAccordion}>
                        {!isOpen ? (labelReadMore ? labelReadMore : 'Läs mer') : 'Stäng'}
                    </a>
                </ReadMore>}
        </BoxWrapper>
    );
};
  


Accordion.propTypes = {
    id: PropTypes.string,
    title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    labelRight: PropTypes.object,
    labelRightComponent: PropTypes.element,
    headerComponent: PropTypes.element,
    footerComponent: PropTypes.element,
    labelReadMore: PropTypes.string,
    unexpandable: PropTypes.bool,
    noHeaderPadding: PropTypes.bool,
    customMargin: PropTypes.string,
    hideOverflow: PropTypes.bool,
    noBackground: PropTypes.bool, 
}

Accordion.defaultProps = {
    hideOverflow: true,
    noBackground: false,
}

export default Accordion;


const boxPropsToExclude = ['extraBottomMargin']

// styles
const BoxWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) => !boxPropsToExclude.includes(prop)
})`
    width: 100%;
    margin-top: 30px;
    border-radius: 5px;
    &:first-of-type {
        margin-top: 0px;
    }
    margin-bottom: 30px;

    padding: 25px 20px ${props => props.extraBottomMargin ? '30px' : '10px'} 20px;
    ${props => mediaQuery.large(`
        padding: 10px 20px ${props.extraBottomMargin ? '15px' : '10px'} 20px;
        margin-top: 15px;
        margin-bottom: 30px;
    `)}

    background-color: ${definitions.colors.blueBackground};
    font-family: ${definitions.fonts.main};
    
    ${mediaQuery.custom(`
        padding: 20px;
    `, '1234px')}

     ${mediaQuery.small(`
        padding: 15px 20px;
        margin-top: 0px;
        margin-bottom: 15px;
    `)}
`;

const HeaderWrapper = styled.div`
    width: 100%; 
    display: inline-flex; 
    align-items: flex-end;

    margin-bottom: 20px;
    font-family: ${definitions.fonts.main};
    
    ${mediaQuery.medium(`
        margin-bottom: 15px;
    `)}

    ${mediaQuery.small(`
        display: initial;
        margin-bottom: 0px;
    `)}
`;

const headerPropsToExclude = ['align']

const HeaderTitle = styled.div.withConfig({
    shouldForwardProp: (prop) => !headerPropsToExclude.includes(prop)
})`
    width: 50%; 
    padding-bottom: ${props => props.align === 'right' ? '3px' : '0'};

    font-size: ${props => props.align === 'right' ? '18px' : '28px'};
    ${props => mediaQuery.small(`
        font-size: ${props.align === 'right' ? '14px' : '22px'};
        width: 100%; 
    `)}

    ${mediaQuery.medium(`
        font-size: 20px;
        line-height: 1.4;
        margin-bottom: 10px;
        width: 100%;

        &:nth-of-type(2) {
            font-size: 16px;
            font-weight: normal;
            line-height: 1;
        }
    `)}

    font-weight: bold;
    font-family: ${definitions.fonts.heading};
    text-align: ${props => props.align === 'right' ? 'right' : 'left'};
    color: white;

    span {
        font-family: ${definitions.fonts.main};
        font-weight: normal; 
	    color: white; 
	}

    ${mediaQuery.small(`
        font-size: 18px;
        line-height: 1.2;
        text-align: left;
        span {
            float: right;
	    }
    `)}
`;

const containerPropsToExclude = ['hideOverflow', 'noPadding']

const Container = styled.div.withConfig({
    shouldForwardProp: (prop) => !containerPropsToExclude.includes(prop)
})`
    padding: ${props => props.noPadding ? '0' : '36px'};

    position: relative;
    overflow: ${props => props.hideOverflow ? 'hidden' : 'visible'};
    border-radius: ${props => props.hideOverflow ? '5px' : 'none'};
    background-color: ${props => props.hideOverflow ? 'white' : 'none'};;

	font-size: 16px;
    font-family: ${definitions.fonts.main};
    text-align: left; 

    ${mediaQuery.medium(`
        padding: 0px;
    `)}
`;

const ContainerBackground = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    border-radius: 5px;
    background-color: white;
`;

const MainContainer = styled.div`
    width:100%; 
    height: auto; 
    overflow: hidden;

    ${clearFix()}

    & > div {
        ${clearFix()}
    }
`;

const readMorePropsToExclude = ['isOpen', 'customMargin']

const ReadMore = styled.div.withConfig({
    shouldForwardProp: (prop) => !readMorePropsToExclude.includes(prop)
})`
    width: 100%; 
    margin: 15px 0 15px 0;
    text-align: right;

    ${props => props.customMargin ? `
        ${mediaQuery.medium(`
            margin: ${props.customMargin};
        `)}
    ` : `
        ${mediaQuery.medium(`
            margin: 0px;
        `)}
    `
    }

    a, a:hover, a:visited, a:focus {
        color: white; 
        text-decoration: none; 
        font-family: ${definitions.fonts.heading};
        font-size: 20px; 
        font-weight: bold;

        ${mediaQuery.medium(`
            font-size: 18px;
        `)}
        ${mediaQuery.small(`
            font-size: 16px;
        `)}
    }

    a:hover {
        text-decoration: underline; 
    }

    a {
        &:after {
            display: inline-block;
            ${iconCode.chevDown()} 
            
            font-size: 0.55em;
            margin-left: 0.8em;
            
            transform: ${(props) => (props.isOpen ? 'rotateZ(-180deg) translateY(0.3em)' : 'none')};
            transition: transform 400ms ease-out; 
        }

        &:hover {
            &:after {
                text-decoration: none;
            }
        }
    }
`;
